import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  Building2,
  MoreVertical,
  Plus,
  Search,
  Phone,
  Mail,
  Edit,
  Trash2,
  Users,
} from "lucide-react";

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const supabase = useSupabase();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyForm, setCompanyForm] = useState({
    name: "",
    contact_email: "",
    contact_phone: "",
    is_active: true,
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from("companies")
        .select(
          `
          *,
          facilities (
            id,
            name,
            location,
            is_active
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCompanies(data);
    } catch (err) {
      setError("Nepodarilo sa načítať spoločnosti");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    // Add validation check for required fields
    if (!companyForm.name.trim()) {
      setError("Názov spoločnosti je povinný");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("companies")
        .insert([
          {
            name: companyForm.name,
            contact_email: companyForm.contact_email,
            contact_phone: companyForm.contact_phone,
            is_active: true,
          },
        ])
        .select();

      if (error) throw error;

      setCompanies([data[0], ...companies]);
      setIsAddingCompany(false);
      setCompanyForm({ name: "", contact_email: "", contact_phone: "" });
    } catch (err) {
      setError("Nepodarilo sa pridať spoločnosť");
      console.error(err);
    }
  };

  const handleEditCompany = async () => {
    try {
      // Start a batch write operation by updating the company first
      const { data: updatedCompany, error: companyError } = await supabase
        .from("companies")
        .update({
          name: companyForm.name,
          contact_email: companyForm.contact_email,
          contact_phone: companyForm.contact_phone,
          is_active: companyForm.is_active,
        })
        .eq("id", selectedCompany.id)
        .select();

      if (companyError) throw companyError;

      // Check if company status changed
      if (!companyForm.is_active) {
        // If company is set to inactive, update all related facilities to inactive
        const { error: facilityError } = await supabase
          .from("facilities")
          .update({ is_active: false })
          .eq("company_id", selectedCompany.id);

        if (facilityError) throw facilityError;
      } else if (companyForm.is_active && !selectedCompany.is_active) {
        // If company is changed from inactive to active, update all related facilities to active
        const { error: facilityError } = await supabase
          .from("facilities")
          .update({ is_active: true })
          .eq("company_id", selectedCompany.id);

        if (facilityError) throw facilityError;
      }

      // Re-fetch the company with updated facilities data
      const { data: refreshedData, error: refreshError } = await supabase
        .from("companies")
        .select(
          `
          *,
          facilities (
            id,
            name,
            location,
            is_active
          )
        `
        )
        .eq("id", selectedCompany.id)
        .single();

      if (refreshError) throw refreshError;

      // Update the local state
      setCompanies(
        companies.map((company) =>
          company.id === selectedCompany.id ? refreshedData : company
        )
      );

      setIsEditingCompany(false);
      setSelectedCompany(null);
      setCompanyForm({ name: "", contact_email: "", contact_phone: "" });
    } catch (err) {
      setError("Nepodarilo sa upraviť spoločnosť");
      console.error(err);
    }
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm("Naozaj chcete vymazať túto spoločnosť?")) return;

    try {
      // Deactivate the company
      const { error: companyError } = await supabase
        .from("companies")
        .update({ is_active: false })
        .eq("id", company.id);

      if (companyError) throw companyError;

      // Deactivate all related facilities
      const { error: facilityError } = await supabase
        .from("facilities")
        .update({ is_active: false })
        .eq("company_id", company.id);

      if (facilityError) throw facilityError;

      // Update local state
      setCompanies(
        companies.map((c) =>
          c.id === company.id ? { ...c, is_active: false } : c
        )
      );
    } catch (err) {
      setError("Nepodarilo sa vymazať spoločnosť");
      console.error(err);
    }
  };

  const handleEditClick = (company) => {
    setSelectedCompany(company);
    setCompanyForm({
      name: company.name,
      contact_email: company.contact_email,
      contact_phone: company.contact_phone,
      is_active: company.is_active, // Add this line
    });
    setIsEditingCompany(true);
  };

  const filteredCompanies = companies.filter(
    (company) =>
      (company.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.contact_email || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (company.contact_phone || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto p-6">
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-bold">
                Správa spoločností
              </CardTitle>
              <CardDescription>
                Spravujte svoje spoločnosti a ich skupiny
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddingCompany(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Pridať spoločnosť
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Vyhľadať spoločnosti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Názov spoločnosti</TableHead>
                  <TableHead>Kontaktné informácie</TableHead>
                  <TableHead>Skupiny</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead>Akcie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell className="font-medium">
                      {company.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="w-4 h-4 mr-2" />
                          {company.contact_email}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="w-4 h-4 mr-2" />
                          {company.contact_phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        <Building2 className="w-3 h-3 mr-1" />
                        {company.facilities?.length || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={company.is_active ? "success" : "secondary"}
                        className={`${
                          company.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {company.is_active ? "Aktívna" : "Neaktívna"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleEditClick(company)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Upraviť
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/companies/${company.id}/facilities`)
                            }
                          >
                            <Building2 className="mr-2 h-4 w-4" />
                            Správa prevádzok
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/companies/${company.id}/managers`)
                            }
                          >
                            <Users className="mr-2 h-4 w-4" />
                            Správa manažérov
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteCompany(company)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Vymazať
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add Company Dialog */}
      <Dialog open={isAddingCompany} onOpenChange={setIsAddingCompany}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pridať novú spoločnosť</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">
                Názov spoločnosti <span className="text-red-600">*</span>
              </Label>
              <Input
                id="name"
                value={companyForm.name}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, name: e.target.value })
                }
                className={
                  !companyForm.name.trim() && error ? "border-red-500" : ""
                }
              />
              {!companyForm.name.trim() && error && (
                <p className="text-sm text-red-500 mt-1">
                  Názov spoločnosti je povinný
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="email">Kontaktný email</Label>
              <Input
                id="email"
                type="email"
                value={companyForm.contact_email}
                onChange={(e) =>
                  setCompanyForm({
                    ...companyForm,
                    contact_email: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="phone">Kontaktný telefón</Label>
              <Input
                id="phone"
                value={companyForm.contact_phone}
                onChange={(e) =>
                  setCompanyForm({
                    ...companyForm,
                    contact_phone: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddingCompany(false)}>
              Zrušiť
            </Button>
            <Button onClick={handleAddCompany}>Pridať spoločnosť</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={isEditingCompany} onOpenChange={setIsEditingCompany}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upraviť spoločnosť</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Názov spoločnosti</Label>
              <Input
                id="edit-name"
                value={companyForm.name}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, name: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Kontaktný email</Label>
              <Input
                id="edit-email"
                type="email"
                value={companyForm.contact_email}
                onChange={(e) =>
                  setCompanyForm({
                    ...companyForm,
                    contact_email: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Kontaktný telefón</Label>
              <Input
                id="edit-phone"
                value={companyForm.contact_phone}
                onChange={(e) =>
                  setCompanyForm({
                    ...companyForm,
                    contact_phone: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Stav spoločnosti</Label>
              <div className="flex items-center space-x-2 rounded-md border p-4">
                <Switch
                  checked={companyForm.is_active}
                  onCheckedChange={(checked) =>
                    setCompanyForm({ ...companyForm, is_active: checked })
                  }
                />
                <div className="space-y-0.5">
                  <Label className="text-base">
                    {companyForm.is_active ? "Aktívna" : "Neaktívna"}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {companyForm.is_active
                      ? "Spoločnosť je aktívna a môže využívať služby"
                      : "Spoločnosť je deaktivovaná a nemôže využívať služby"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditingCompany(false)}
            >
              Zrušiť
            </Button>
            <Button onClick={handleEditCompany}>Uložiť zmeny</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default CompanyDashboard;
